import React, {
  useMemo, useState, forwardRef, memo
} from 'react';
import { Form, Spacer } from 'components/atoms'
import { Formik, Field } from 'formik';
import {
  FieldArrayDndFormik, FieldImageGalleryUpload, TabsLineList, FieldCustom, FieldSelect, FieldToggle
} from 'components/molecules';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { useDebounce } from 'react-use'
import { useFormikForwardRef } from 'hooks'
import { linkRegex } from 'services/constants'
import { useParams } from 'react-router-dom'
import styled from 'styled-components/macro';
import { generateKey } from 'services/generators'
import SliderCell from './SliderCell'

const types = {
  image: 'image',
  video: 'video',
}

// Schemas
const Schema = Yup.object().shape({
  list: Yup.array()
    .required('Campo obrigatório')
});

const ImageSchema = Yup.object().shape({
  type: Yup.string().required('Campo obrigatório'),
  url: Yup.string().matches(linkRegex, 'O URL inserido não é válido. Certifique-se que começa com http:// ou https:// para sites fora do portal ou com /{lingua}/... para ligações internas.')
    .required('Campo obrigatório'),
  description: Yup.string().required('Campo obrigatório'),
  image: Yup.object({
    url: Yup.string(),
    alt: Yup.string(),
  }).required('Campo obrigatório'),
  hasWarning: Yup.boolean(),
  warningDescription: Yup.string().nullable().notRequired(),
  displayTab: Yup.boolean(),
  useInterval: Yup.boolean(),
  startDate: Yup.lazy(value => (Yup.date()
    .when('useInterval', {
      is: (value) => (value && value === true),
      then: () => (Yup.string().required('Campo obrigatório')),
      otherwise: () => (Yup.string().nullable(true))
    }))),
  endDate: Yup.lazy(value => (Yup.date()
    .when('useInterval', {
      is: (value) => (value && value === true),
      then: () => (Yup.string().required('Campo obrigatório')),
      otherwise: () => (Yup.string().nullable(true))
    })))
});

const VideoSchema = Yup.object().shape({
  image: Yup.object({
    url: Yup.string(),
    alt: Yup.string(),
  }).required('Campo obrigatório'),
  default: Yup.string().matches(linkRegex, 'O URL inserido não é válido. Certifique-se que começa com http:// ou https:// para sites fora do portal ou com /{lingua}/... para ligações internas.')
    .required('Campo obrigatório'),
  title: Yup.string().required('Campo obrigatório'),
  accessible: Yup.string().matches(linkRegex, 'O URL inserido não é válido. Certifique-se que começa com http:// ou https:// para sites fora do portal ou com /{lingua}/... para ligações internas.'),
  useInterval: Yup.boolean(),
  startDate: Yup.lazy(value => (Yup.date()
    .when('useInterval', {
      is: (value) => (value && value === true),
      then: () => (Yup.string().required('Campo obrigatório')),
      otherwise: () => (Yup.string().nullable(true))
    }))),
  endDate: Yup.lazy(value => (Yup.date()
    .when('useInterval', {
      is: (value) => (value && value === true),
      then: () => (Yup.string().required('Campo obrigatório')),
      otherwise: () => (Yup.string().nullable(true))
    })))
});

// eslint-disable-next-line react/prop-types
const SliderItemForm = forwardRef(({ initialValues, onSubmit }, ref) => {
  // this is necessary for all FieldArrayDnd forms
  // in order to control the submitForm from parent
  const formRef = useFormikForwardRef(ref);
  const { lang } = useParams()

  // eslint-disable-next-line react/prop-types
  const { type: initialType } = initialValues;
  const initialTab = initialType === types.video ? initialType : types.image
  const [tab, setTab] = useState(initialTab)

  const initialValuesVideo = useMemo(() => {
    if (initialValues.type === 'video') return initialValues

    // convert image to video schema
    const { image, ...restImage } = initialValues
    return {
      ...restImage,
      type: 'video',
      image,
      // eslint-disable-next-line react/prop-types
      placeholder: image && image.url ? image.url : '',
      // eslint-disable-next-line react/prop-types
      alt: image && image.alt ? image.alt : '',
      default: '',
      accessible: '',
      useInterval: false,
      startDate: null,
      endDate: null
    }
  }, [initialValues])

  const initialValuesImage = useMemo(() => {
    if (initialValues.type !== 'video') return initialValues

    const {
      placeholder, alt, ...restVideo
    } = initialValues
    return {
      ...restVideo,
      type: 'default',
      image: {
        url: placeholder,
        alt,
      },
      title: '',
      description: '',
      url: '',
      displayTab: false,
      useInterval: false,
      startDate: null,
      endDate: null
    }
  }, [initialValues])

  const tabs = [
    {
      onClick: () => setTab(types.image),
      text: 'Imagem',
      isActive: tab === types.image,
    },
    {
      onClick: () => setTab(types.video),
      text: 'Video',
      isActive: tab === types.video,
    }
  ]

  const renderTab = () => {
    switch (tab) {
    case types.video: {
      return (
        <Formik
          ref={formRef}
          initialValues={initialValuesVideo}
          validationSchema={VideoSchema}
          onSubmit={(values, { setSubmitting }) => {
            onSubmit({
              ...values,
              type: types.video,
              ...(values.useInterval === false ? {
                startDate: null,
                endDate: null
              } : {
                startDate: values.startDate,
                endDate: values.endDate
              })
            });
            setSubmitting(false);
          }}
          validateOnMount
          enableReinitialize
        >
          {({ values }) => (
            <Form>
              <Field
                title="Imagem de Capa"
                name="image"
                component={FieldImageGalleryUpload}
              />
              <Field
                title="Titulo"
                name="title"
                component={FieldCustom}
              />
              <Field
                title="Url do vídeo"
                name="default"
                component={FieldCustom}
              />
              <Field
                title="Url de vídeo para acessibilidade"
                name="accessible"
                info="Video alternativo com áudio-descrição e/ou língua gestual"
                component={FieldCustom}
              />
              <Field
                title="Agendar intervalo de publicação / permanência"
                name="useInterval"
                component={FieldToggle}
              />
              {
                values.useInterval === true && (
                  <>
                    <Field
                      title="Data de inicio"
                      name="startDate"
                      component={FieldCustom}
                      type="datetime-local"
                    />
                    <Field
                      title="Data de fim"
                      name="endDate"
                      component={FieldCustom}
                      type="datetime-local"
                    />
                  </>
                )
              }
            </Form>
          )}
        </Formik>
      )
    }
    default: {
      return (
        <Formik
          ref={formRef}
          initialValues={initialValuesImage}
          validationSchema={ImageSchema}
          onSubmit={(values, { setSubmitting }) => {
            onSubmit({
              ...values,
              hasWarning: Boolean(values.warningDescription),
              warningTitle: (lang === 'pt' ? 'Aviso' : 'warnings'),
              ...(values.useInterval === false ? {
                startDate: null,
                endDate: null
              } : {
                startDate: values.startDate,
                endDate: values.endDate
              })
            });
            setSubmitting(false);
          }}
          validateOnMount
          enableReinitialize
        >
          {({ values }) => (
            <Form>
              <Field
                title="Modelo"
                name="type"
                component={FieldSelect}
                placeholder="Selecione o modelo..."
                options={[
                  { value: 'default', label: 'Titulo no rodapé' },
                  { value: 'leftColumns', label: 'Titulo lateral com opção de Aviso' },
                ]}
              />
              <Field
                title="Imagem"
                name="image"
                component={FieldImageGalleryUpload}
              />
              <Field
                title="Titulo"
                name="title"
                component={FieldCustom}
              />
              {values.type !== 'leftColumns' && (
                <Field
                  title="Antetítulo"
                  name="description"
                  component={FieldCustom}
                />
              )}
              <Field
                title="Link"
                name="url"
                component={FieldCustom}
              />
              <Field
                title="Abrir num novo tab"
                name="displayTab"
                component={FieldToggle}
              />
              {values.type === 'leftColumns' && (
                <>
                  <Spacer top="m" />
                  <Field
                    title="Aviso"
                    name="warningDescription"
                    component={FieldCustom}
                  />
                </>
              )}
              <Field
                title="Agendar intervalo de publicação / permanência"
                name="useInterval"
                component={FieldToggle}
              />
              {
                values.useInterval === true && (
                  <>
                    <Field
                      title="Data de inicio"
                      name="startDate"
                      component={FieldCustom}
                      type="datetime-local"
                    />
                    <Field
                      title="Data de fim"
                      name="endDate"
                      component={FieldCustom}
                      type="datetime-local"
                    />
                  </>
                )
              }
            </Form>
          )}
        </Formik>

      )
    }
    }
  }
  return (
    <>
      <TabsWrapper>
        <TabsLineList tabs={tabs} modifiers={['stretch', 'primary']} />
      </TabsWrapper>
      <Spacer top="xl" />
      {renderTab()}
    </>
  )
})

const SliderEditor = memo(({ initialProps, onChange }) => {
  const [editorProps, setEditorProps] = useState(initialProps)
  const initialValues = useMemo(() => {
    const { data: { list } } = initialProps;
    const computedList = list.map((item, index) => {
      const { type } = item;
      const { placeholder, alt } = item;
      if (type === types.video) {
        return {
          ...item,
          index,
          type,
          image: { url: placeholder, alt },
          tab: types.video
        }
      }
      return {
        ...item,
        index,
        type: type || 'default',
        tab: types.image
      }
    });
    return {
      list: computedList
    };
  }, [initialProps]);

  const handleFormChange = newData => {
    const { list } = newData
    const newList = list.map((item) => {
      const { title, type, useInterval } = item
      if (type === types.video) {
        return {
          placeholder: item.image.url,
          alt: item.image.alt,
          default: item.default,
          accessible: item.accessible,
          title,
          type,
        }
      }

      return item
    });

    const newProps = {
      ...initialProps,
      data: { list: newList }
    }
    const isValid = Schema.isValidSync(newData)
    if (isValid) setEditorProps(newProps)
  };

  useDebounce(
    () => {
      onChange(editorProps);
    },
    300,
    [editorProps]
  )

  const columns = useMemo(() => ([
    {
      accessor: 'image.url',
      Cell: SliderCell,
    },
    {
      accessor: 'title',
    },
  ]), [])

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={Schema}
      validate={handleFormChange}
    >
      <Form>
        <Field
          title="Itens do carrossel"
          name="list"
          component={FieldArrayDndFormik}
          columns={columns}
          rowKey={(item) => generateKey(item.title)}
          ItemForm={SliderItemForm}
          shouldAddOnTop
          shouldHideHeaders
        />
      </Form>
    </Formik>
  );
});

SliderEditor.propTypes = {
  onChange: PropTypes.func.isRequired,
  initialProps: PropTypes.shape({
    data: PropTypes.string,
    lang: PropTypes.string,
    displayTab: PropTypes.bool
  }),
};

SliderEditor.defaultProps = {
  initialProps: {},
  displayTab: false
};

const TabsWrapper = styled.div`
  border-bottom: 1px solid rgba(238, 238, 238, 0.933);
`

export default SliderEditor;
